import { IdNamePair } from "../../../customLists/customListView/CustomListView.types.ts";
import { EntityType } from "../../Common.types.ts";
import { InputFieldTypeEnum } from "../../../entityTemplates/inputFields/InputFields.types.ts";
import { SelectableInputField } from "../../../entityTemplates/EntityTemplate.types.ts";
import { ContractColumn } from "../../../contract/contractDisplayFields/ContractDisplayFields.types.ts";
import { SystemColumn } from "../../../system/systems/systemDisplayFields/SystemDisplayFields.types.ts";
import { FilterOperatorUi } from "@common/api/filterTypes.ts";

export interface FilterOption {
    id: string;
    label: string;
    type: FilterOptionType;
    dataUrl?: string;
    // When set, a SelectField renders a server-searched picker that queries `${searchUrl}?query=<text>`
    // returning Array<IdNamePair>. Used for large cross-entity lists where neither fixedData nor a
    // single entityType search fits.
    searchUrl?: string;
    value: any; // The raw JSON value
    // Picking a value replaces the previous one instead of adding to it. The value stays an array so
    // chips, persistence and operator matching are unaffected. For filters backed by a server parameter
    // that only accepts one value, where letting the user tick several would silently ignore all but one.
    singleSelect?: boolean;
    // For filters backed by a server parameter that only takes the picked values, where offering "is not" would
    // silently be read as "is".
    hideFilterOperator?: boolean;
    fixedData?: Array<IdNamePair>;
    entityType?: EntityType;
    customInputType?: InputFieldTypeEnum;
    grouping?: FieldGroupingType;
    hideNoDate?: boolean;
    hideFutureDates?: boolean;
    // Leaves a DateField as just its from/to pickers. For a date whose useful ranges are arbitrary rather than
    // relative to today, where the presets would only offer periods that do not apply.
    hideDatePresets?: boolean;
    filterOperator?: FilterOperatorUi;
}

// The date picker's calendar is a popper inside the submenu paper rather than a portal, so the paper's own scrolling
// clips it. A preset list makes the paper tall enough to hide that; with only the two inputs the paper is barely
// taller than them, and the calendar would open into a scrollbar over the "to" field. Both places that host a
// submenu have to let it overflow - the popover's own menu and the filter bar's chip editor.
export function needsRoomForOpenCalendar(option: FilterOption | undefined): boolean {
    return option?.type === FilterOptionType.DateField && !!option.hideDatePresets;
}

export enum FilterOptionType {
    SelectField = 1,
    TextField = 2,
    DateField = 3,
    Boolean = 4,
    Currency = 5,
    Amount = 6,
    Number = 7,
    RecurrenceField = 8,
    RelatedEntityField = 9,
}

export enum ContractFieldGrouping {
    Contract = "contract",
    Access = "access",
    Period = "period",
    Terms = "terms",
    ContractParties = "contractParties",
    CustomFields = "customFields",
}

export enum ActivityFieldGrouping {
    IdentityAndBasics = "identityAndBasics",
    DescriptionAndScope = "descriptionAndScope",
    Scheduling = "scheduling",
    PeopleAndOwnership = "peopleAndOwnership",
    Organization = "organization",
    ContractsAndRelations = "contractsAndRelations",
    StatusTimeline = "statusTimeline",
}

export enum SystemFieldGrouping {
    System = "system",
    DataAndStorage = "dataAndStorage",
    SystemInformation = "systemInformation",
    AI = "aiHeader",
    Access = "access",
    CustomFields = "customFields",
    Supplier = "supplier",
    AssociatedSystems = "associatedAssets",
    Policies = "policies",
    PersonalData = "personalData",
}

export enum TaskFieldGrouping {
    Task = "task",
    Scheduling = "scheduling",
    PeopleAndOrganisation = "peopleAndOrganisation",
    Classification = "classification",
    Relations = "relations",
}

export enum PolicyFieldGrouping {
    Policy = "policy",
}

export enum RiskFieldGrouping {
    Risk = "risk",
}

export enum UserFieldGrouping {
    User = "users",
}

// The value is the translation key the popover labels the group with.
export enum FrameworkFieldGrouping {
    Framework = "frameworks",
}

// The value is the translation key the popover labels the group with.
export enum AuditFieldGrouping {
    InternalAssessment = "internalAssessments",
    InternalAssessmentGroup = "internalAssessmentTabGroups",
    ExternalAuditGroup = "auditGroups",
}

// The value is the translation key the popover labels the group with.
export enum FrameworkControlFieldGrouping {
    Control = "controls",
}

// The values are the translation keys the popover labels each group with, so they name existing keys rather than
// reading as identifiers: "vendorManagementPlanName" is the product name, "sharings" is headed "Transfers".
export enum LegalEntityFieldGrouping {
    LegalEntity = "legalEntity",
    VendorManagement = "vendorManagementPlanName",
    Transfers = "sharings",
    Documents = "documents",
    ProcessingActivities = "processingActivities",
    Risk = "risk",
    CustomFields = "customFields",
}

// The values are the translation keys the popover labels each group with, mostly the names of the template steps the
// fields are filled in on.
export enum IncidentLogFieldGrouping {
    Incident = "incident",
    Organisation = "organization",
    Details = "details",
    Timeline = "timeline",
    DataAndDataSubjects = "dataAndDataSubjects",
    ServicesAndImpact = "incidentLogServicesAndImpact",
    ReportingToAuthorities = "reportToAuthorities",
    NotifyingAffectedParties = "incidentLogNotifyingAffectedParties",
    ConsequencesAndMeasures = "incidentLogConsequencesAndMeasures",
    Request = "incidentLogRequest",
    Response = "incidentLogResponse",
    Access = "access",
    CustomFields = "customFields",
}

// The values are the translation keys the popover labels each group with: "sharings" is headed "Transfers" and
// "affiliationHeader" "Association", like the step.
export enum ProcessingActivityFieldGrouping {
    ProcessingActivity = "processingActivity",
    Association = "affiliationHeader",
    PersonalData = "personalData",
    Relations = "relations",
    Transfers = "sharings",
    Documents = "documents",
    Compliance = "compliance",
}

// The values are the translation keys the popover labels each group with: "sharing" is headed "Transfer".
export enum SharingFieldGrouping {
    Sharing = "sharing",
    ProcessingActivity = "processingActivity",
    Sender = "sender",
    Recipient = "recipient",
    Documents = "documents",
}

// The values are the translation keys the popover labels each group with.
export enum ProcessFieldGrouping {
    Process = "process",
    Access = "access",
    Relations = "relations",
    Risk = "risk",
    CustomFields = "customFields",
}

export type FieldGroupingType =
    | ContractFieldGrouping
    | ActivityFieldGrouping
    | SystemFieldGrouping
    | TaskFieldGrouping
    | PolicyFieldGrouping
    | RiskFieldGrouping
    | UserFieldGrouping
    | FrameworkFieldGrouping
    | AuditFieldGrouping
    | FrameworkControlFieldGrouping
    | LegalEntityFieldGrouping
    | IncidentLogFieldGrouping
    | ProcessingActivityFieldGrouping
    | SharingFieldGrouping
    | ProcessFieldGrouping;
export const ContractFieldGroupingOrder: Array<FieldGroupingType> = [
    ContractFieldGrouping.Contract,
    ContractFieldGrouping.ContractParties,
    ContractFieldGrouping.Access,
    ContractFieldGrouping.Period,
    ContractFieldGrouping.Terms,
    ContractFieldGrouping.CustomFields,
];

export const ActivityFieldGroupingOrder: Array<FieldGroupingType> = [
    ActivityFieldGrouping.IdentityAndBasics,
    ActivityFieldGrouping.DescriptionAndScope,
    ActivityFieldGrouping.Scheduling,
    ActivityFieldGrouping.PeopleAndOwnership,
    ActivityFieldGrouping.Organization,
    ActivityFieldGrouping.ContractsAndRelations,
    ActivityFieldGrouping.StatusTimeline,
];

export const SystemFieldGroupingOrder: Array<FieldGroupingType> = [
    SystemFieldGrouping.System,
    SystemFieldGrouping.Access,
    SystemFieldGrouping.DataAndStorage,
    SystemFieldGrouping.Supplier,
    SystemFieldGrouping.SystemInformation,
    SystemFieldGrouping.AI,
    SystemFieldGrouping.AssociatedSystems,
    SystemFieldGrouping.Policies,
    SystemFieldGrouping.PersonalData,
    SystemFieldGrouping.CustomFields,
];

export const PolicyFieldGroupingOrder: Array<FieldGroupingType> = [PolicyFieldGrouping.Policy];

export const RiskFieldGroupingOrder: Array<FieldGroupingType> = [RiskFieldGrouping.Risk];

export const UserFieldGroupingOrder: Array<FieldGroupingType> = [UserFieldGrouping.User];

export const FrameworkFieldGroupingOrder: Array<FieldGroupingType> = [FrameworkFieldGrouping.Framework];

export const InternalAssessmentFieldGroupingOrder: Array<FieldGroupingType> = [AuditFieldGrouping.InternalAssessment];

export const InternalAssessmentGroupFieldGroupingOrder: Array<FieldGroupingType> = [AuditFieldGrouping.InternalAssessmentGroup];

export const ExternalAuditGroupFieldGroupingOrder: Array<FieldGroupingType> = [AuditFieldGrouping.ExternalAuditGroup];

export const FrameworkControlFieldGroupingOrder: Array<FieldGroupingType> = [FrameworkControlFieldGrouping.Control];

export const LegalEntityFieldGroupingOrder: Array<FieldGroupingType> = [
    LegalEntityFieldGrouping.LegalEntity,
    LegalEntityFieldGrouping.VendorManagement,
    LegalEntityFieldGrouping.Transfers,
    LegalEntityFieldGrouping.Documents,
    LegalEntityFieldGrouping.ProcessingActivities,
    LegalEntityFieldGrouping.Risk,
    LegalEntityFieldGrouping.CustomFields,
];

export const ProcessingActivityFieldGroupingOrder: Array<FieldGroupingType> = [
    ProcessingActivityFieldGrouping.ProcessingActivity,
    ProcessingActivityFieldGrouping.Association,
    ProcessingActivityFieldGrouping.PersonalData,
    ProcessingActivityFieldGrouping.Relations,
    ProcessingActivityFieldGrouping.Transfers,
    ProcessingActivityFieldGrouping.Documents,
    ProcessingActivityFieldGrouping.Compliance,
];

export const SharingFieldGroupingOrder: Array<FieldGroupingType> = [
    SharingFieldGrouping.Sharing,
    SharingFieldGrouping.ProcessingActivity,
    SharingFieldGrouping.Sender,
    SharingFieldGrouping.Recipient,
    SharingFieldGrouping.Documents,
];

export const IncidentLogFieldGroupingOrder: Array<FieldGroupingType> = [
    IncidentLogFieldGrouping.Incident,
    IncidentLogFieldGrouping.Organisation,
    IncidentLogFieldGrouping.Details,
    IncidentLogFieldGrouping.Access,
    IncidentLogFieldGrouping.Timeline,
    IncidentLogFieldGrouping.DataAndDataSubjects,
    IncidentLogFieldGrouping.ServicesAndImpact,
    IncidentLogFieldGrouping.ReportingToAuthorities,
    IncidentLogFieldGrouping.NotifyingAffectedParties,
    IncidentLogFieldGrouping.ConsequencesAndMeasures,
    IncidentLogFieldGrouping.Request,
    IncidentLogFieldGrouping.Response,
    IncidentLogFieldGrouping.CustomFields,
];

export const ProcessFieldGroupingOrder: Array<FieldGroupingType> = [
    ProcessFieldGrouping.Process,
    ProcessFieldGrouping.Access,
    ProcessFieldGrouping.Relations,
    ProcessFieldGrouping.Risk,
    ProcessFieldGrouping.CustomFields,
];

export const TaskFieldGroupingOrder: Array<FieldGroupingType> = [
    TaskFieldGrouping.Task,
    TaskFieldGrouping.Scheduling,
    TaskFieldGrouping.PeopleAndOrganisation,
    TaskFieldGrouping.Classification,
    TaskFieldGrouping.Relations,
];

export interface SystemFieldAndGrouping {
    translation: string;
    grouping: SystemFieldGrouping;
    column?: SystemColumn;
}

export interface SystemInputFieldsMapping {
    systemFieldsAndGroupings: Record<string, SystemFieldAndGrouping>;
    customFieldsSelectableValues: Array<SelectableInputField>;
}

export interface ContractInputFieldsMapping {
    contractFieldsAndGroupings: Record<string, ContractFieldAndGrouping>;
    customFieldsSelectableValues: Array<SelectableInputField>;
}

export interface ContractFieldAndGrouping {
    translation: string;
    grouping: ContractFieldGrouping;
    column?: ContractColumn;
}
